import { confirmResetPassword, resetPassword } from "aws-amplify/auth";
import { useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { AuthLayout } from "../components/AuthLayout";
import { ErrorNotice, Field, useAction } from "../components/ui";

export function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { pending, error, run } = useAction();
  const [email, setEmail] = useState(params.get("email") ?? "");
  const [step, setStep] = useState<"email" | "code">("email");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [resent, setResent] = useState(false);

  // Cognito answers the same whether or not the email has a login, so this never reveals who does.
  const submitEmail = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      await resetPassword({ username: email.trim().toLowerCase() });
      setStep("code");
    });
  };

  const resend = () => {
    setResent(false);
    void run(async () => {
      await resetPassword({ username: email.trim().toLowerCase() });
      setResent(true);
    });
  };

  const submitNew = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      await confirmResetPassword({ username: email.trim().toLowerCase(), confirmationCode: code.trim(), newPassword: password });
      navigate(`/login?email=${encodeURIComponent(email.trim().toLowerCase())}&reset=done`, { replace: true });
    });
  };

  return (
    <AuthLayout
      masthead={
        <h1 className="display">
          A new <em>key</em>.
        </h1>
      }
    >
      {step === "email" ? (
        <form className="form" onSubmit={submitEmail}>
          <div className="page-head">
            <p className="label">Step 1 of 2</p>
            <h2 className="title">
              Reset your <em>password</em>
            </h2>
            <p className="lede">We'll email you a code to set a new one.</p>
          </div>
          <Field label="Email" type="email" autoComplete="email" required autoFocus value={email} onChange={(e) => setEmail(e.target.value)} />
          <ErrorNotice error={error} />
          <button className="btn block" disabled={pending}>
            {pending ? "Sending…" : "Send code"}
          </button>
          <p className="sheet-foot">
            Remembered it? <Link to={`/login?email=${encodeURIComponent(email.trim().toLowerCase())}`}>Log in</Link>
          </p>
        </form>
      ) : (
        <form className="form" onSubmit={submitNew}>
          <div className="page-head">
            <p className="label">Step 2 of 2</p>
            <h2 className="title">
              Check your <em>inbox</em>
            </h2>
            <p className="lede">
              If <strong>{email}</strong> has a login, we've sent it a 6-digit code.
            </p>
          </div>
          <Field
            label="Code"
            className="code"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            required
            autoFocus
            value={code}
            onChange={(e) => setCode(e.target.value)}
          />
          <Field
            label="New password"
            type="password"
            autoComplete="new-password"
            required
            minLength={12}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            hint="12+ characters, with upper and lower case, a number and a symbol."
          />
          <ErrorNotice error={error} />
          {resent && <p className="notice ok">A new code is on its way.</p>}
          <button className="btn block" disabled={pending}>
            {pending ? "Saving…" : "Set new password"}
          </button>
          <p className="sheet-foot">
            No email?{" "}
            <button type="button" className="link-btn" onClick={resend} disabled={pending}>
              Send another code
            </button>
          </p>
        </form>
      )}
    </AuthLayout>
  );
}
